/**
 * Testing helper utilities - thin wrappers around Jest and MSW
 */

import type { RequestHandler } from 'msw'
import { http, HttpResponse } from 'msw'
import { getGlobalTestServer } from './setup'

/**
 * Create a mock API handler - simple wrapper around MSW's http handlers
 */
export function createMockHandler(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  url: string,
  response: any,
  options: {
    status?: number
    delay?: number
    headers?: Record<string, string>
  } = {}
): RequestHandler {
  const { status = 200, delay = 0, headers = {} } = options

  return http[method.toLowerCase() as keyof typeof http](url, async () => {
    // Apply delay if specified
    if (delay > 0) {
      await new Promise(resolve => setTimeout(resolve, delay))
    }

    return HttpResponse.json(response, {
      status,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
    })
  }) as RequestHandler
}

/**
 * Create a mock error handler
 */
export function createMockErrorHandler(
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  url: string,
  error: {
    status: number
    code: string
    message: string
    details?: any
  },
  options: { delay?: number } = {}
): RequestHandler {
  return createMockHandler(method, url, {
    success: false,
    error,
  }, {
    status: error.status,
    delay: options.delay || 0,
  })
}

/**
 * Add handlers to the test server - wrapper around MSW's server.use()
 */
export function addTestHandlers(...handlers: RequestHandler[]): void {
  const server = getGlobalTestServer()
  if (server) {
    server.use(...handlers)
  } else {
    throw new Error('MSW test server not available. Make sure to call setupMSWJest() or setupMSWVitest() in your test setup.')
  }
}

/**
 * Reset test handlers - wrapper around MSW's server.resetHandlers()
 */
export function resetTestHandlers(): void {
  const server = getGlobalTestServer()
  if (server) {
    server.resetHandlers()
  }
}

/**
 * Quick utilities for common test scenarios
 */
export const testUtils: Record<string, any> = {
  /**
   * Create a successful API response handler
   */
  mockSuccess: (method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', url: string, data: any) => {
    return createMockHandler(method, url, {
      success: true,
      data,
    })
  },

  /**
   * Create an error API response handler
   */
  mockError: (method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', url: string, error: any) => {
    return createMockErrorHandler(method, url, error)
  },

  /**
   * Create a delayed response handler (for testing loading states)
   */
  mockLoading: (method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE', url: string, data: any, delay: number = 1000) => {
    return createMockHandler(method, url, {
      success: true,
      data,
    }, { delay })
  },

  /**
   * Create test data using a factory function
   */
  createTestData: <T>(factory: () => T, count: number = 1): T[] => {
    return Array.from({ length: count }, factory)
  },
}
